import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    Svg,
    Line,
    Polygon,
    Rect,
    Circle,
    Path,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import SCHOOL_LOGO from "./zpa.png";

/* ============================================================
   ZION PRAISE ACADEMY — PREMIUM CERTIFICATE
   Theme: Elegant Black, Gold, Champagne & Ivory
   Landscape A4 | Existing download/data flow preserved
============================================================ */

const INSTITUTE_NAME = "ZION PRAISE ACADEMY";
const INSTITUTE_SUBTITLE = "CRM SCHOOL SYSTEM";
const INSTITUTE_ADDRESS = "MONROVIA, LIBERIA";

const CERTIFICATE_TITLE = "CERTIFICATE OF COMPLETION";
const CERTIFICATE_CREDENTIAL = "ACADEMIC ACHIEVEMENT & EXCELLENCE";

const COLORS = {
    black: "#111111",
    deepBlack: "#080808",
    charcoal: "#242424",
    softBlack: "#35312A",

    gold: "#C6A04A",
    richGold: "#A98127",
    brightGold: "#E5C66E",
    champagne: "#F0DDA6",
    paleGold: "#FAF3DF",

    ivory: "#FFFDF7",
    white: "#FFFFFF",
    cream: "#F9F5EA",

    text: "#28251F",
    muted: "#777064",
    border: "#D8C79D",
    lightBorder: "#E9E0CC",
};

/* ============================================================
   DATA HELPERS
============================================================ */

const getDisplayValue = (value) => {
    if (value === null || value === undefined) return "";

    if (typeof value === "object") {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            ""
        );
    }

    return String(value);
};

const getStudentName = (student) =>
    getDisplayValue(student?.full_name) ||
    getDisplayValue(student?.name) ||
    getDisplayValue(student?.student_name) ||
    "Graduate";

const getClassName = (gradeClass) =>
    getDisplayValue(gradeClass?.name) || "Academic Program";

const getDivision = (gradeClass) =>
    getDisplayValue(gradeClass?.division);

const getAcademicYear = (academicYear) =>
    getDisplayValue(academicYear?.name) || "Academic Year";

const getCertificateNumber = (student, academicYear) => {
    const year = getAcademicYear(academicYear);

    const yearDigits =
        String(year).replace(/[^0-9]/g, "").slice(-4) ||
        "2026";

    const studentId =
        student?.id || student?.student_id || "001";

    return `ZPA-${yearDigits}-${studentId}`;
};

/* ============================================================
   CERTIFICATE STYLES
============================================================ */

const styles = StyleSheet.create({
    page: {
        width: "100%",
        height: "100%",
        padding: 20,
        backgroundColor: COLORS.ivory,
        fontFamily: "Helvetica",
    },

    outerFrame: {
        width: "100%",
        height: "100%",
        padding: 5,
        borderWidth: 2.5,
        borderColor: COLORS.black,
        backgroundColor: COLORS.paleGold,
    },

    goldFrame: {
        width: "100%",
        height: "100%",
        padding: 3,
        borderWidth: 1.5,
        borderColor: COLORS.gold,
        backgroundColor: COLORS.ivory,
    },

    innerFrame: {
        flex: 1,
        borderWidth: 1,
        borderColor: COLORS.lightBorder,
        backgroundColor: COLORS.ivory,
        position: "relative",
        overflow: "hidden",
    },

    topBand: {
        height: 11,
        width: "100%",
        flexDirection: "row",
    },

    topBlack: {
        width: "68%",
        backgroundColor: COLORS.deepBlack,
    },

    topGold: {
        width: "24%",
        backgroundColor: COLORS.gold,
    },

    topChampagne: {
        width: "8%",
        backgroundColor: COLORS.champagne,
    },

    bottomBand: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 8,
        flexDirection: "row",
    },

    bottomBlack: {
        width: "62%",
        backgroundColor: COLORS.deepBlack,
    },

    bottomGold: {
        width: "30%",
        backgroundColor: COLORS.gold,
    },

    bottomChampagne: {
        width: "8%",
        backgroundColor: COLORS.champagne,
    },

    content: {
        flex: 1,
        paddingHorizontal: 42,
        paddingTop: 13,
        paddingBottom: 13,
    },

    /* HEADER */

    header: {
        width: "100%",
        minHeight: 60,
        flexDirection: "row",
        alignItems: "center",
    },

    logoArea: {
        width: 66,
        height: 60,
        alignItems: "center",
        justifyContent: "center",
    },

    logo: {
        width: 53,
        height: 53,
        objectFit: "contain",
    },

    organizationArea: {
        flex: 1,
        paddingLeft: 9,
        justifyContent: "center",
    },

    organization: {
        color: COLORS.black,
        fontSize: 19,
        fontWeight: "bold",
        letterSpacing: 1.5,
    },

    organizationSub: {
        marginTop: 4,
        color: COLORS.richGold,
        fontSize: 8,
        fontWeight: "bold",
        letterSpacing: 2.1,
    },

    address: {
        marginTop: 4,
        color: COLORS.muted,
        fontSize: 6.2,
        letterSpacing: 1,
    },

    headerMark: {
        width: 130,
        height: 54,
        alignItems: "flex-end",
        justifyContent: "center",
    },

    headerMarkLabel: {
        marginTop: 4,
        color: COLORS.richGold,
        fontSize: 5.7,
        fontWeight: "bold",
        letterSpacing: 1.05,
    },

    /* HEADING */

    headingArea: {
        width: "100%",
        alignItems: "center",
        marginTop: 8,
    },

    credential: {
        color: COLORS.richGold,
        fontSize: 7,
        fontWeight: "bold",
        letterSpacing: 2.5,
        textAlign: "center",
    },

    certificateTitle: {
        marginTop: 4,
        color: COLORS.black,
        fontSize: 26,
        fontWeight: "bold",
        letterSpacing: 1.4,
        textAlign: "center",
    },

    divider: {
        width: 340,
        height: 15,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    dividerLine: {
        width: 140,
        height: 1,
        backgroundColor: COLORS.border,
    },

    dividerDiamond: {
        width: 9,
        height: 9,
        marginHorizontal: 9,
        backgroundColor: COLORS.gold,
        transform: [{ rotate: "45deg" }],
    },

    dividerCenter: {
        width: 5,
        height: 5,
        marginHorizontal: 4,
        backgroundColor: COLORS.black,
        transform: [{ rotate: "45deg" }],
    },

    /* RECIPIENT */

    recipientSection: {
        width: "100%",
        alignItems: "center",
        marginTop: 1,
        paddingHorizontal: 10,
    },

    bodyIntro: {
        color: COLORS.muted,
        fontSize: 8,
        letterSpacing: 0.8,
        textAlign: "center",
    },

    studentName: {
        marginTop: 5,
        color: COLORS.black,
        fontSize: 30,
        fontWeight: "bold",
        letterSpacing: 0.7,
        textAlign: "center",
        maxWidth: 600,
    },

    nameDecoration: {
        width: 400,
        height: 13,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    nameLine: {
        width: 165,
        height: 1,
        backgroundColor: COLORS.border,
    },

    nameDiamond: {
        width: 8,
        height: 8,
        marginHorizontal: 10,
        backgroundColor: COLORS.gold,
        transform: [{ rotate: "45deg" }],
    },

    /* RECOGNITION */

    recognitionArea: {
        alignItems: "center",
        marginTop: 1,
    },

    recognition: {
        maxWidth: 540,
        color: COLORS.text,
        fontSize: 8,
        lineHeight: 1.5,
        textAlign: "center",
    },

    /* PROGRAM PANEL */

    programPanel: {
        width: "100%",
        minHeight: 48,
        marginTop: 9,
        flexDirection: "row",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.cream,
    },

    programAccent: {
        width: 7,
        backgroundColor: COLORS.black,
    },

    programMain: {
        flex: 1,
        justifyContent: "center",
        paddingHorizontal: 14,
        paddingVertical: 7,
    },

    programLabel: {
        color: COLORS.richGold,
        fontSize: 6,
        fontWeight: "bold",
        letterSpacing: 1.4,
    },

    programName: {
        marginTop: 3,
        color: COLORS.black,
        fontSize: 11.5,
        fontWeight: "bold",
        letterSpacing: 0.3,
    },

    division: {
        marginTop: 2,
        color: COLORS.muted,
        fontSize: 6.5,
    },

    yearPanel: {
        width: 130,
        borderLeftWidth: 1,
        borderLeftColor: COLORS.border,
        backgroundColor: COLORS.paleGold,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 5,
    },

    yearLabel: {
        color: COLORS.muted,
        fontSize: 5.8,
        fontWeight: "bold",
        letterSpacing: 1.2,
    },

    yearValue: {
        marginTop: 4,
        color: COLORS.black,
        fontSize: 9.5,
        fontWeight: "bold",
        textAlign: "center",
    },

    /* CERTIFICATE DETAILS */

    lowerArea: {
        width: "100%",
        minHeight: 47,
        marginTop: 6,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    informationBox: {
        width: 176,
        height: 36,
        paddingHorizontal: 10,
        justifyContent: "center",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.ivory,
    },

    informationLabel: {
        color: COLORS.muted,
        fontSize: 5.3,
        letterSpacing: 0.8,
    },

    informationValue: {
        marginTop: 3,
        color: COLORS.black,
        fontSize: 7,
        fontWeight: "bold",
        letterSpacing: 0.5,
    },

    badgeArea: {
        flex: 1,
        height: 46,
        alignItems: "center",
        justifyContent: "center",
    },

    badge: {
        width: 47,
        height: 47,
    },

    completionBox: {
        width: 176,
        height: 36,
        paddingHorizontal: 10,
        justifyContent: "center",
        alignItems: "flex-end",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.paleGold,
    },

    completionLabel: {
        color: COLORS.muted,
        fontSize: 5.3,
        letterSpacing: 0.7,
    },

    completionValue: {
        marginTop: 3,
        color: COLORS.richGold,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 0.45,
    },

    /* SIGNATURES */

    signatures: {
        width: "100%",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginTop: 7,
    },

    signature: {
        width: 175,
        alignItems: "center",
    },

    signatureLine: {
        width: 148,
        borderTopWidth: 1,
        borderTopColor: COLORS.black,
        marginBottom: 4,
    },

    signatureName: {
        color: COLORS.black,
        fontSize: 6.4,
        fontWeight: "bold",
        letterSpacing: 0.5,
        textAlign: "center",
    },

    signatureRole: {
        marginTop: 2,
        color: COLORS.richGold,
        fontSize: 5.3,
        letterSpacing: 0.45,
        textAlign: "center",
    },

    centerMark: {
        width: 155,
        alignItems: "center",
        justifyContent: "center",
    },

    centerMarkText: {
        color: COLORS.black,
        fontSize: 6,
        fontWeight: "bold",
        letterSpacing: 1.3,
        textAlign: "center",
    },

    centerMarkSub: {
        marginTop: 3,
        color: COLORS.richGold,
        fontSize: 5,
        letterSpacing: 0.4,
        textAlign: "center",
    },

    footerNote: {
        marginTop: 5,
        alignItems: "center",
    },

    footerNoteText: {
        color: COLORS.muted,
        fontSize: 4.8,
        letterSpacing: 0.65,
        textAlign: "center",
    },
});

/* ============================================================
   DECORATIVE CORNERS
============================================================ */

const CertificateCorner = ({ flipX = false, flipY = false }) => {
    const transform = [];

    if (flipX) transform.push({ scaleX: -1 });
    if (flipY) transform.push({ scaleY: -1 });

    return (
        <Svg
            width="83"
            height="66"
            viewBox="0 0 83 66"
            style={{ transform }}
        >
            <Polygon
                points="0,0 83,0 0,66"
                fill={COLORS.black}
            />

            <Polygon
                points="0,0 60,0 0,48"
                fill={COLORS.richGold}
            />

            <Polygon
                points="0,0 35,0 0,28"
                fill={COLORS.champagne}
            />

            <Line
                x1="8"
                y1="10"
                x2="53"
                y2="10"
                stroke={COLORS.white}
                strokeWidth="0.8"
                opacity="0.65"
            />

            <Line
                x1="8"
                y1="10"
                x2="8"
                y2="49"
                stroke={COLORS.white}
                strokeWidth="0.8"
                opacity="0.65"
            />

            <Rect
                x="7"
                y="7"
                width="5"
                height="5"
                fill={COLORS.white}
            />

            <Circle
                cx="23"
                cy="23"
                r="2"
                fill={COLORS.champagne}
            />

            <Circle
                cx="31"
                cy="31"
                r="1.5"
                fill={COLORS.white}
                opacity="0.75"
            />
        </Svg>
    );
};

/* ============================================================
   SCHOOL EMBLEM
============================================================ */

const SchoolBadge = () => (
    <Svg width="47" height="47" viewBox="0 0 100 100">
        <Circle cx="50" cy="50" r="47" fill={COLORS.black} />

        <Circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke={COLORS.gold}
            strokeWidth="2.5"
        />

        <Circle
            cx="50"
            cy="50"
            r="35"
            fill="none"
            stroke={COLORS.champagne}
            strokeWidth="1"
        />

        <Polygon
            points="50,15 58,35 80,35 63,49 69,71 50,58 31,71 37,49 20,35 42,35"
            fill={COLORS.gold}
        />

        <Path
            d="M35 48 L45 57 L65 37"
            fill="none"
            stroke={COLORS.white}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />

        <Line
            x1="31"
            y1="78"
            x2="69"
            y2="78"
            stroke={COLORS.champagne}
            strokeWidth="2"
        />
    </Svg>
);

/* ============================================================
   DECORATIVE HEADER MARK
============================================================ */

const HeaderMark = () => (
    <Svg width="125" height="40" viewBox="0 0 125 40">
        <Line
            x1="24"
            y1="7"
            x2="125"
            y2="7"
            stroke={COLORS.black}
            strokeWidth="2.5"
        />

        <Line
            x1="42"
            y1="15"
            x2="125"
            y2="15"
            stroke={COLORS.gold}
            strokeWidth="2"
        />

        <Line
            x1="60"
            y1="23"
            x2="125"
            y2="23"
            stroke={COLORS.border}
            strokeWidth="1.5"
        />

        <Polygon
            points="10,5 20,15 10,25 0,15"
            fill={COLORS.gold}
        />

        <Polygon
            points="30,12 36,18 30,24 24,18"
            fill={COLORS.black}
        />

        <Circle cx="54" cy="33" r="2.5" fill={COLORS.black} />
        <Circle cx="66" cy="33" r="2.5" fill={COLORS.gold} />
        <Circle cx="78" cy="33" r="2.5" fill={COLORS.richGold} />

        <Line
            x1="88"
            y1="33"
            x2="125"
            y2="33"
            stroke={COLORS.border}
            strokeWidth="1"
        />
    </Svg>
);

/* ============================================================
   CERTIFICATE PAGE
============================================================ */

const CertificatePage = ({ student, gradeClass, academicYear }) => {
    const studentName = getStudentName(student);
    const className = getClassName(gradeClass);
    const division = getDivision(gradeClass);
    const year = getAcademicYear(academicYear);

    const certificateNumber = getCertificateNumber(
        student,
        academicYear
    );

    return (
        <Document
            title={`${studentName} - Zion Praise Academy Certificate`}
            author={INSTITUTE_NAME}
            creator={INSTITUTE_NAME}
            subject="Certificate of Completion"
        >
            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
            >
                <View style={styles.outerFrame}>
                    <View style={styles.goldFrame}>
                        <View style={styles.innerFrame}>
                            {/* TOP BAND */}

                            <View style={styles.topBand}>
                                <View style={styles.topBlack} />
                                <View style={styles.topGold} />
                                <View style={styles.topChampagne} />
                            </View>

                            {/* CORNER ORNAMENTS */}

                            <View
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 11,
                                }}
                            >
                                <CertificateCorner />
                            </View>

                            <View
                                style={{
                                    position: "absolute",
                                    right: 0,
                                    top: 11,
                                }}
                            >
                                <CertificateCorner flipX />
                            </View>

                            <View
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    bottom: 8,
                                }}
                            >
                                <CertificateCorner flipY />
                            </View>

                            <View
                                style={{
                                    position: "absolute",
                                    right: 0,
                                    bottom: 8,
                                }}
                            >
                                <CertificateCorner flipX flipY />
                            </View>

                            <View style={styles.content}>
                                {/* SCHOOL HEADER */}

                                <View style={styles.header}>
                                    <View style={styles.logoArea}>
                                        <Image
                                            src={SCHOOL_LOGO}
                                            style={styles.logo}
                                        />
                                    </View>

                                    <View style={styles.organizationArea}>
                                        <Text style={styles.organization}>
                                            {INSTITUTE_NAME}
                                        </Text>

                                        <Text style={styles.organizationSub}>
                                            {INSTITUTE_SUBTITLE}
                                        </Text>

                                        <Text style={styles.address}>
                                            {INSTITUTE_ADDRESS}
                                        </Text>
                                    </View>

                                    <View style={styles.headerMark}>
                                        <HeaderMark />

                                        <Text style={styles.headerMarkLabel}>
                                            EXCELLENCE • CHARACTER • FUTURE
                                        </Text>
                                    </View>
                                </View>

                                {/* CERTIFICATE TITLE */}

                                <View style={styles.headingArea}>
                                    <Text style={styles.credential}>
                                        {CERTIFICATE_CREDENTIAL}
                                    </Text>

                                    <Text style={styles.certificateTitle}>
                                        {CERTIFICATE_TITLE}
                                    </Text>

                                    <View style={styles.divider}>
                                        <View style={styles.dividerLine} />
                                        <View style={styles.dividerDiamond} />
                                        <View style={styles.dividerCenter} />
                                        <View style={styles.dividerDiamond} />
                                        <View style={styles.dividerLine} />
                                    </View>
                                </View>

                                {/* STUDENT NAME */}

                                <View style={styles.recipientSection}>
                                    <Text style={styles.bodyIntro}>
                                        This certificate is proudly awarded to
                                    </Text>

                                    <Text style={styles.studentName}>
                                        {studentName}
                                    </Text>

                                    <View style={styles.nameDecoration}>
                                        <View style={styles.nameLine} />
                                        <View style={styles.nameDiamond} />
                                        <View style={styles.nameLine} />
                                    </View>
                                </View>

                                {/* CITATION */}

                                <View style={styles.recognitionArea}>
                                    <Text style={styles.recognition}>
                                        In recognition of successfully completing
                                        the prescribed academic requirements and
                                        demonstrating knowledge, discipline,
                                        character, dedication, and a commitment
                                        to excellence as a student of Zion Praise
                                        Academy – CRM School System.
                                    </Text>
                                </View>

                                {/* ACADEMIC PROGRAM */}

                                <View style={styles.programPanel}>
                                    <View style={styles.programAccent} />

                                    <View style={styles.programMain}>
                                        <Text style={styles.programLabel}>
                                            ACADEMIC PROGRAM / GRADE
                                        </Text>

                                        <Text style={styles.programName}>
                                            {className}
                                        </Text>

                                        {division ? (
                                            <Text style={styles.division}>
                                                {division}
                                            </Text>
                                        ) : null}
                                    </View>

                                    <View style={styles.yearPanel}>
                                        <Text style={styles.yearLabel}>
                                            ACADEMIC YEAR
                                        </Text>

                                        <Text style={styles.yearValue}>
                                            {year}
                                        </Text>
                                    </View>
                                </View>

                                {/* CERTIFICATE INFORMATION */}

                                <View style={styles.lowerArea}>
                                    <View style={styles.informationBox}>
                                        <Text style={styles.informationLabel}>
                                            CERTIFICATE NUMBER
                                        </Text>

                                        <Text style={styles.informationValue}>
                                            {certificateNumber}
                                        </Text>
                                    </View>

                                    <View style={styles.badgeArea}>
                                        <SchoolBadge />
                                    </View>

                                    <View style={styles.completionBox}>
                                        <Text style={styles.completionLabel}>
                                            CERTIFICATION STATUS
                                        </Text>

                                        <Text style={styles.completionValue}>
                                            PROGRAM COMPLETED
                                        </Text>
                                    </View>
                                </View>

                                {/* AUTHORIZED SIGNATURES */}

                                <View style={styles.signatures}>
                                    <View style={styles.signature}>
                                        <View style={styles.signatureLine} />

                                        <Text style={styles.signatureName}>
                                            SCHOOL ADMINISTRATOR
                                        </Text>

                                        <Text style={styles.signatureRole}>
                                            ZION PRAISE ACADEMY
                                        </Text>
                                    </View>

                                    <View style={styles.centerMark}>
                                        <Text style={styles.centerMarkText}>
                                            ZION PRAISE ACADEMY
                                        </Text>

                                        <Text style={styles.centerMarkText}>
                                            CRM SCHOOL SYSTEM
                                        </Text>

                                        <Text style={styles.centerMarkSub}>
                                            Excellence • Character • Knowledge • Future
                                        </Text>
                                    </View>

                                    <View style={styles.signature}>
                                        <View style={styles.signatureLine} />

                                        <Text style={styles.signatureName}>
                                            ACADEMIC REGISTRAR
                                        </Text>

                                        <Text style={styles.signatureRole}>
                                            ACADEMIC RECORDS
                                        </Text>
                                    </View>
                                </View>

                                {/* FOOTER */}

                                <View style={styles.footerNote}>
                                    <Text style={styles.footerNoteText}>
                                        ZION PRAISE ACADEMY – CRM SCHOOL SYSTEM
                                        {"  "}•{"  "}
                                        EXCELLENCE • CHARACTER • KNOWLEDGE • FUTURE
                                    </Text>
                                </View>
                            </View>

                            {/* BOTTOM BAND */}

                            <View style={styles.bottomBand}>
                                <View style={styles.bottomBlack} />
                                <View style={styles.bottomGold} />
                                <View style={styles.bottomChampagne} />
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

/* ============================================================
   DOWNLOAD SERVICE
   Existing function signatures and data flow preserved
============================================================ */

const GraduateCertificatePDF = {
    downloadSingle: async (student, gradeClass, academicYear) => {
        const blob = await pdf(
            <CertificatePage
                student={student}
                gradeClass={gradeClass}
                academicYear={academicYear}
            />
        ).toBlob();

        const fileName =
            `${getStudentName(student).replace(/\s+/g, "_")}` +
            `_Zion_Praise_Academy_Certificate.pdf`;

        saveAs(blob, fileName);
    },

    downloadMultiple: async (students, gradeClass, academicYear) => {
        const zip = new JSZip();

        for (const student of students) {
            const blob = await pdf(
                <CertificatePage
                    student={student}
                    gradeClass={gradeClass}
                    academicYear={academicYear}
                />
            ).toBlob();

            const fileName =
                `${getStudentName(student).replace(/\s+/g, "_")}` +
                `_Zion_Praise_Academy_Certificate.pdf`;

            zip.file(fileName, blob);
        }

        const zipBlob = await zip.generateAsync({
            type: "blob",
        });

        saveAs(
            zipBlob,
            "Zion_Praise_Academy_Certificates.zip"
        );
    },
};

export default GraduateCertificatePDF;
